import {
  getMonthLongAbbreviation,
  isMonthYearBeforeOrEqual,
  isMonthYearAfterOrEqual,
  MonthYear,
} from '@common/neo.tax';
import {
  Typography,
  AutocompleteInput,
  AutocompleteInputProps,
  DefaultComponentProps,
  GeneralFormError,
  InputFormContainer,
  InputFormContainerProps,
  Stack,
  colors,
} from '@ui-components/neo.tax';
import React, { useEffect } from 'react';

import { defaultMonthOptions, defaultYearOptions } from './month-year-picker.constants';
import { isOptionDisabled } from './month-year-picker.utils';

export type MonthYearPickerProps = DefaultComponentProps &
  Pick<InputFormContainerProps, 'helpText' | 'label' | 'secondaryLabel'> &
  Pick<AutocompleteInputProps<Record<string, any>>, 'disabled'> & {
    id: string;
    clearButtonId?: string;
    max?: MonthYear;
    min?: MonthYear;
    monthOptions?: { label: string; value: number }[];
    yearOptions?: { label: string; value: number }[];
    onChange?: (monthYear: MonthYear | null) => void;
    onMonthChange?: (month: number | null) => void;
    onYearChange?: (year: number | null) => void;
    value?: MonthYear;
    clearable?: boolean;
    error?: boolean;
    errorText?: string;
    monthError?: boolean;
    monthErrorText?: string;
    monthPlaceholder?: string;
    yearError?: boolean;
    yearErrorText?: string;
    yearPlaceholder?: string;
  };

export const MonthYearPicker = ({
  clearButtonId,
  disabled,
  error,
  errorText,
  monthError,
  monthErrorText,
  yearError,
  yearErrorText,
  helpText,
  id,
  label,
  max,
  min,
  monthOptions = defaultMonthOptions,
  yearOptions = defaultYearOptions,
  onChange,
  secondaryLabel,
  value,
  clearable,
  monthPlaceholder = '--',
  yearPlaceholder = '--',
}: MonthYearPickerProps): React.ReactElement => {
  const [month, setMonth] = React.useState<number>();
  const [year, setYear] = React.useState<number>();
  const hasValue = month !== undefined || year !== undefined;

  const notifyIfComplete = (month: number | undefined, year: number | undefined): void => {
    // ONLY call `onChange` if both month and year are cleared or number
    // Subscribers shouldn't care about half-set values; it's all or nothing
    if (typeof month !== 'number' && typeof year !== 'number') {
      onChange?.(null);
      return;
    }
    if (typeof month === 'number' && typeof year === 'number') {
      onChange?.({
        month,
        year,
      });
      return;
    }
  };

  const handleMonthChange = (newMonthValue: number | undefined): void => {
    setMonth(newMonthValue);
    notifyIfComplete(newMonthValue, year);
  };

  const handleYearChange = (newYearValue: number | undefined): void => {
    setYear(newYearValue);
    notifyIfComplete(month, newYearValue);
  };

  const handleClear = (): void => {
    setMonth(undefined);
    setYear(undefined);
    notifyIfComplete(undefined, undefined);
  };

  useEffect(() => {
    setMonth(value?.month);
    setYear(value?.year);
  }, [value]);

  const monthMinErrorText =
    !!value && !!min && !isMonthYearAfterOrEqual(value, min)
      ? `Must be during or after ${getMonthLongAbbreviation(min.month)} ${min.year}`
      : '';
  const monthMaxErrorText =
    !!value && !!max && !isMonthYearBeforeOrEqual(value, max)
      ? `Must be during or before ${getMonthLongAbbreviation(max.month)} ${max.year}`
      : '';
  const errorMessage = errorText || monthErrorText || monthMinErrorText || monthMaxErrorText || yearErrorText;
  const hasError = !!(error || monthError || monthMinErrorText || monthMaxErrorText || yearError);

  return (
    <InputFormContainer
      inputId={id}
      label={label}
      helpText={helpText}
      secondaryLabel={secondaryLabel}
      action={
        clearable &&
        hasValue && (
          <Typography as="button" fontSize="sm" color={colors.primary[600]} onClick={handleClear} id={clearButtonId}>
            Clear
          </Typography>
        )
      }
    >
      <Stack direction="row" gap={2}>
        <AutocompleteInput<number>
          id={`${id}-month`}
          options={monthOptions.map((option) => ({
            ...option,
            disabled: typeof year === 'undefined' ? false : isOptionDisabled({ month: option.value, year }, min, max),
          }))}
          value={month}
          onChange={handleMonthChange}
          placeholder={monthPlaceholder}
          disabled={disabled}
          error={error || monthError || !!monthMinErrorText || !!monthMaxErrorText}
        />
        <AutocompleteInput<number>
          id={`${id}-year`}
          options={yearOptions.map((option) => ({
            ...option,
            disabled: (!!min && option.value < min.year) || (!!max && option.value > max.year),
          }))}
          value={year}
          onChange={handleYearChange}
          placeholder={yearPlaceholder}
          disabled={disabled}
          error={error || yearError}
        />
      </Stack>
      {/* The fields may be not be wide enough to show error text on a single line so use one error for both */}
      <GeneralFormError error={hasError} id={`${id}-error`}>
        {errorMessage}
      </GeneralFormError>
    </InputFormContainer>
  );
};
